<script setup>
import { useForm } from 'vee-validate'
import { storeToRefs } from 'pinia'
import * as yup from 'yup'
import { useAuthStore } from '~/stores/auth'
import { useMessageStore } from '~/stores/messages'
import { isEqualUnorderedArray } from '~/utils/assertions'
import { checkCustomerAge, checkDateFormat } from '~/utils/validators'

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Load user data
const bearerToken = useState('state_bearerToken')
const oid = useState('state_userOid')
const correlationId = useState('state_appCorrelationId')

// Load Auth status
const authStore = useAuthStore()

const { userStatus } = storeToRefs(authStore)

const {
    updateUserStatus,
    updateProfileSuccessCallback,
    updateProfileErrorCallback,
    updateProfileOnSettledCallback,
    updateProfileOnMutateCallback
} = authStore

// Fetch user data
// const { data: user, suspense: userSuspense, isError: isUserError } = useFetchProfile({ bearerToken, oid, correlationId })
const { data: user, isError: isUserError, isLoading: isUserLoading } = useFetchProfile({ bearerToken, oid, correlationId })

// Uncommented it to avoid large loading times in webviews (for android and ios app!)
/*
onServerPrefetch(async () => {
    await userSuspense()
})
*/

// Handles error or failed server status code  response
if (isUserError.value || user.value?.status === 'failed') {
    updateUserStatus({
        success: false,
        message: staticKeys.value?.account_form_error_load,
    })
}

// Compute current user
const currentUser = computed(() => user.value?.data)

// Prepare form (prefill this values, mandatory)
const { values, meta, handleSubmit, isSubmitting } = useForm({
    firstName: '',
    lastName: '',
    birthDate: '',
    billingAddress: {
        postalCode: ''
    },
    salutationCode: ''
})

// Add dynamic values (use empty strings, because without this the form is not submitted)
const getFirstName = computed(() => currentUser.value?.firstName || '')
const getLastName = computed(() => currentUser.value?.lastName || '')
const getBirthday = computed(() => currentUser.value?.birthDate || '')
const getPostCode = computed(() => currentUser.value?.billingAddress?.postalCode || '')
const getGender = computed(() => currentUser.value?.salutationCode || '')
const isPending = computed(() => userIsPending.value || isSubmitting.value || isUserLoading.value)

// Define initial user data state (used to show submit button)
const initialStateArray = computed(() => {
    return [
        getFirstName.value,
        getLastName.value,
        getGender.value,
        getBirthday.value,
        getPostCode.value
    ]
})

// Show submit button depending on user data + errors
const isVisible = computed(() => {
    // User has not yet entered anything
    if (Object.keys(values).length === 0) {
        return false
    }

    // User has entered different data
    const useHasEnteredDifferentData = !isEqualUnorderedArray(initialStateArray.value, Object.values(values).flat())

    // Show a button at least success message is visible
    return (useHasEnteredDifferentData && meta.value?.valid) || userStatus.value?.success
})

// Define config objects for form fields
const firstNameConfig = computed(() => {
    return {
        label: staticKeys.value?.account_form_firstname_label,
        fieldType: 'text',
        id: 'firstName',
        name: 'firstName',
        required: 'required',
        validationRules: yup.string()
            .required(staticKeys.value?.account_form_firstname_error_message)
            .max(40, staticKeys.value?.general_error_maxlength.replace('%s', '40')),
        pending: isPending.value,
        val: getFirstName.value
    }
})

const lastNameConfig = computed(() => {
    return {
        label: staticKeys.value?.account_form_lastname_label,
        fieldType: 'text',
        id: 'lastName',
        name: 'lastName',
        pending: isPending.value,
        val: getLastName.value
    }
})

const birthdayConfig = computed(() => {
    return {
        label: staticKeys.value?.account_form_birthday_label,
        fieldType: 'date',
        clickable: true,
        id: 'birthDate',
        name: 'birthDate',
        validationRules: yup.string()
            .test(
                'validateBirthDate',
                staticKeys.value?.account_form_birthday_error_message,
                value => checkCustomerAge(value)
            )
            .test(
                'validDateFormat',
                staticKeys.value?.general_date_validation_error_message,
                value => checkDateFormat(value)
            ),
        pending: isPending.value,
        val: getBirthday.value,
        helperText: staticKeys.value?.account_form_birthday_headline
    }
})

const postCodeConfig = computed(() => {
    return {
        label: staticKeys.value?.account_form_postalcode_label,
        placeholder: staticKeys.value?.account_form_postalcode_placeholder,
        fieldType: 'text',
        id: 'postalCode',
        name: 'postalCode',
        validationRules: yup.string()
            .matches(/^\d{5}$/, {
                excludeEmptyString: true,
                message: staticKeys.value?.account_form_postalcode_error_message
            }),
        pending: isPending.value,
        val: getPostCode.value
    }
})

const genderConfig = computed(() => {
    return {
        female: {
            label: staticKeys.value?.account_form_gender_female_label,
            name: 'salutationCode',
            id: 'female',
            value: 'female',
            val: getGender.value
        },
        male: {
            label: staticKeys.value?.account_form_gender_male_label,
            name: 'salutationCode',
            value: 'male',
            id: 'male',
            val: getGender.value
        },
        diverse: {
            label: staticKeys.value?.account_form_gender_divers_label,
            name: 'salutationCode',
            value: 'diverse',
            id: 'diverse',
            val: getGender.value
        }
    }
})

const isFormSubmitting = computed(() => isSubmitting.value || userStatus.value?.success)

const getFormClass = computed(() => {
    return {
        'is-submitting': isFormSubmitting.value
    }
})

const submitButtonConfig = computed(() => {
    return {
        visible: isVisible.value,
        label: staticKeys.value?.account_form_button_label_savechanges,
        buttonConfig: {
            type: 'submit',
            buttonType: 'primary',
            isPending: isFormSubmitting.value,
            active: true
        }
    }
})

// Fetch mutation
const { mutate, isPending: userIsPending } = useUpdateProfileMutation({
    bearerToken,
    oid,
    onSuccessCallback: () => updateProfileSuccessCallback({ successMessage: staticKeys.value?.account_form_personaldata_success_message }),
    onErrorCallback: () => updateProfileErrorCallback({ errorMessage: staticKeys.value?.account_form_update_error, oid }),
    onMutateCallback: updateProfileOnMutateCallback,
    onSettledCallback: () => updateProfileOnSettledCallback({ oid }),
})

/**
 *  Handles form submit
 */
const onSubmit = handleSubmit(async (values) => {
    // Transform user object in the required format
    const transformedUserValues = getFormattedFormUserData(values)

    // Fire update request
    mutate({
        body: transformedUserValues,
        oid
    })

    // Scroll to top sidebar if scroll container exists
    document.querySelector('.sidebar-content-inner')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
    })
})
</script>

<template>
    <form class="account-personal-data-form" :class="getFormClass" @submit.prevent="onSubmit">
        <FormSection>
            <FormText :input-config="firstNameConfig" />
            <FormText :input-config="lastNameConfig" />
        </FormSection>

        <FormSection>
            <FormRadioGroup>
                <FormRadio :radio-button-config="genderConfig?.female" />
                <FormRadio :radio-button-config="genderConfig?.male" />
                <FormRadio :radio-button-config="genderConfig?.diverse" />
            </FormRadioGroup>
        </FormSection>

        <FormSection>
            <FormText :input-config="birthdayConfig" />
        </FormSection>

        <FormSection>
            <FormText :input-config="postCodeConfig" />
        </FormSection>

        <FormButton :form-button-config="submitButtonConfig" />
    </form>
</template>

<style lang="scss">
.account-personal-data-form {
  &::after {
    content: '';
    transition: opacity 400ms ease;
    opacity: 0;
  }

  &.is-submitting {
    @include form-is-submitting;
  }
}
</style>
